"use client";

/**
 * ArchetypeCardBeacon — Sprint 9 (ADR-017 §A.2) viewport beacon for the
 * landing ArchetypeGrid. Renders the card chrome (`<li>`) and fires
 * `archetype_card_viewed` (Plausible only) the first time the card is at
 * least 50% in view for ≥ 250 ms in the session.
 *
 * All fire/dedup rules live in the pure controller
 * lib/analytics/archetype-card-view.ts (unit-tested in node); this island
 * only wires an IntersectionObserver to it. The parent ArchetypeGrid stays
 * a server component, so the JS payload added to the landing route stays
 * small (ADR-017 §Consequences: ≤ 2 kB gzipped target).
 *
 * Spec: docs/03-funnel/analytics-events.md (archetype_card_viewed row)
 *       docs/09-adr/adr-017-sprint9-analytics-hero-copy-inline-quiz-preview-style.md §A.2
 */

import { useEffect, useRef } from "react";
import { track } from "@/lib/analytics";
import {
  VIEW_THRESHOLD,
  createCardViewTracker,
} from "@/lib/analytics/archetype-card-view";
import type { Angle } from "@/lib/content/homepage-variants";

interface Props {
  slug: string;
  /** 1-based index within the rendered grid. */
  position: number;
  variantId: string;
  angle: Angle;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

export function ArchetypeCardBeacon({
  slug,
  position,
  variantId,
  angle,
  className,
  style,
  children,
}: Props) {
  const ref = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;

    const tracker = createCardViewTracker(
      { slug, position, variantId, angle },
      {
        dispatch: ({ slug: s, position: p, variant_id, angle: a }) =>
          track("archetype_card_viewed", {
            slug: s,
            position: p,
            variant_id,
            angle: a,
          }),
        session:
          typeof window !== "undefined" ? window.sessionStorage : null,
      },
    );

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          tracker.onIntersection(entry.isIntersecting, entry.intersectionRatio);
        }
        if (tracker.hasFired()) observer.disconnect();
      },
      { threshold: VIEW_THRESHOLD, rootMargin: "0px" },
    );

    observer.observe(el);

    return () => {
      tracker.dispose();
      observer.disconnect();
    };
  }, [slug, position, variantId, angle]);

  return (
    <li ref={ref} className={className} style={style}>
      {children}
    </li>
  );
}
